import React, { useState, useEffect } from 'react'
import * as api from '../../../app/axios/api.jsx'
import { toast } from 'sonner'
import { getCookie } from '../../../app/axios/CookieConfig.jsx'
import { Trash2, X, Plus } from 'lucide-react'

const FinancePage = () => {
  const profileId = getCookie('id')

  const [showForm, setShowForm] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [formData, setFormData] = useState({
    amount: '',
    transaction_type: '',
    transaction_at: '',
    remark: '',
    com_department: '',
    submitted_by: profileId,
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    // Validation
    if (
      !formData.amount ||
      !formData.transaction_type ||
      !formData.transaction_at ||
      !formData.com_department
    ) {
      toast.error('Please fill all required fields')
      setLoading(false)
      return
    }

    // Convert datetime-local to ISO format with timezone
    const transactionDate = new Date(formData.transaction_at)
    const isoDate = transactionDate.toISOString()

    const newTransaction = {
      amount: parseInt(formData.amount),
      transaction_type: formData.transaction_type,
      transaction_at: isoDate,
      remark: formData.remark,
      com_department: formData.com_department,
      submitted_by: parseInt(formData.submitted_by),
    }

    try {
      await api.createLiquidity(newTransaction)
      toast.success('Transaction added successfully!')

      // Reset form
      setFormData({
        amount: '',
        transaction_type: '',
        transaction_at: '',
        remark: '',
        com_department: '',
        submitted_by: profileId,
      })

      setShowForm(false)
      fetchTransactions()
    } catch (error) {
      console.error('Error creating transaction:', error)
      toast.error('Failed to add transaction')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = (id) => {
    setDeleteId(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    setLoading(true)
    try {
      await api.deleteLiquidity(deleteId)
      toast.success('Transaction deleted successfully!')
      fetchTransactions()
      setShowDeleteModal(false)
      setDeleteId(null)
    } catch (error) {
      console.error('Error deleting transaction:', error)
      toast.error('Failed to delete transaction')
    } finally {
      setLoading(false)
    }
  }

  const fetchTransactions = async () => {
    setFetchLoading(true)
    try {
      const res = await api.fetchLiquidity()
      setTransactions(res.data)
    } catch (error) {
      console.error('Error fetching transactions:', error)
    } finally {
      setFetchLoading(false)
    }
  }

  useEffect(() => {
    fetchTransactions()
  }, [])

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-4 md:p-6'>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='bg-white rounded-lg shadow-lg p-3 sm:p-4 md:p-6 mb-4 md:mb-6'>
          <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4'>
            <div>
              <h1 className='text-xl sm:text-2xl md:text-3xl font-bold text-gray-800'>
                Finance Management
              </h1>
              <p className='text-sm sm:text-base text-gray-600 mt-1'>
                Track and manage all financial transactions
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className='w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition shadow-lg flex items-center justify-center gap-2'
            >
              <Plus size={20} />
              Add Transaction
            </button>
          </div>
        </div>

        {/* Add Transaction Modal */}
        {showForm && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
              <div className='sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-center'>
                <h2 className='text-xl sm:text-2xl font-semibold text-gray-800'>
                  Add New Transaction
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false)
                    setFormData({
                      amount: '',
                      transaction_type: '',
                      transaction_at: '',
                      remark: '',
                      com_department: '',
                      submitted_by: profileId,
                    })
                  }}
                  className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                >
                  <X size={24} className='text-gray-600' />
                </button>
              </div>

              <form onSubmit={handleSubmit} className='p-4 sm:p-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Amount *
                    </label>
                    <input
                      type='number'
                      name='amount'
                      value={formData.amount}
                      onChange={handleInputChange}
                      required
                      min='0'
                      step='1'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='Enter amount'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Transaction Type *
                    </label>
                    <input
                      type='text'
                      name='transaction_type'
                      value={formData.transaction_type}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                      placeholder='e.g., Salary, Purchase, Fees'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Transaction Date *
                    </label>
                    <input
                      type='datetime-local'
                      name='transaction_at'
                      value={formData.transaction_at}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Department *
                    </label>
                    <select
                      name='com_department'
                      value={formData.com_department}
                      onChange={handleInputChange}
                      required
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                    >
                      <option value=''>Select department</option>
                      <option value='software'>Software</option>
                      <option value='electronics'>Electronics</option>
                      <option value='education'>Education</option>
                    </select>
                  </div>

                  <div className='sm:col-span-2'>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Remark
                    </label>
                    <textarea
                      name='remark'
                      value={formData.remark}
                      onChange={handleInputChange}
                      rows='3'
                      className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none'
                      placeholder='Add any additional notes...'
                    />
                  </div>
                </div>

                <button
                  type='submit'
                  disabled={loading}
                  className='w-full mt-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg'
                >
                  {loading ? 'Creating...' : 'Create Transaction'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg shadow-2xl max-w-md w-full p-6'>
              <div className='flex items-center gap-3 mb-4'>
                <div className='p-3 bg-red-100 rounded-full'>
                  <Trash2 size={24} className='text-red-600' />
                </div>
                <h3 className='text-xl font-semibold text-gray-800'>
                  Confirm Delete
                </h3>
              </div>
              <p className='text-gray-600 mb-6'>
                Are you sure you want to delete this transaction? This action
                cannot be undone.
              </p>
              <div className='flex gap-3 justify-end'>
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setDeleteId(null)
                  }}
                  className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className='px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
                  disabled={loading}
                >
                  <Trash2 size={16} />
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Excel-style Table */}
        <div className='bg-white rounded-lg shadow-lg overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='bg-gradient-to-r from-indigo-600 to-purple-600 text-white'>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Amount
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Type
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Transaction Date
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Department
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Remark
                  </th>
                  <th className='px-3 py-3 text-left text-xs sm:text-sm font-semibold border border-gray-300'>
                    Submitted By
                  </th>

                  <th className='px-3 py-3 text-center text-xs sm:text-sm font-semibold border border-gray-300'>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {fetchLoading ? (
                  <tr>
                    <td
                      colSpan='8'
                      className='px-3 py-8 text-center text-gray-600 border border-gray-300'
                    >
                      Loading transactions...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan='8'
                      className='px-3 py-8 text-center text-gray-600 border border-gray-300'
                    >
                      No transactions yet. Add your first transaction!
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                    >
                      <td className='px-3 py-3 text-xs sm:text-sm font-semibold text-green-600 border border-gray-300'>
                        ₹{transaction.amount.toLocaleString('en-IN')}
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm border border-gray-300'>
                        <span className='px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium'>
                          {transaction.transaction_type}
                        </span>
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300 whitespace-nowrap'>
                        {new Date(transaction.transaction_at).toLocaleString()}
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm border border-gray-300'>
                        <span className='px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium capitalize'>
                          {transaction.com_department}
                        </span>
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300 max-w-xs'>
                        <div
                          className='truncate'
                          title={transaction.remark || 'No remark'}
                        >
                          {transaction.remark || 'No remark'}
                        </div>
                      </td>
                      <td className='px-3 py-3 text-xs sm:text-sm text-gray-800 border border-gray-300'>
                        {transaction.submitted_by}
                      </td>

                      <td className='px-3 py-3 text-center border border-gray-300'>
                        <button
                          onClick={() => handleDeleteClick(transaction.id)}
                          className='px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors flex items-center gap-1 mx-auto'
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FinancePage
